import { PopoverPlacement } from "@idui/react-popover";
import clsx from "clsx";
import { FC, useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { FramerTooltip } from "@ryde/controls";
import { StationRouteContext, useCrudEditContext } from "@ryde/redux";
import {
  distanceFormula,
  getStationRouteDurationMin,
  IStationRouteDto,
  NO_VALUE_DISPLAY,
  onActivateKeyDown,
  PROVIDER_OPTIONS,
  resolveStationRouteDirectionsService,
  useDrawers,
  useHasPermissions,
  useTimezone,
} from "@ryde/shared";

import { TabsBottomInfoItemStyled } from "../../../right";
import { HeaderLine2Styled } from "./station-route.header.styled";

export const StationRouteHeaderInfos: FC = () => {
  const { t } = useTranslation();
  const { timezone } = useTimezone();
  const {
    data: { dto: stationRouteDto },
  } = useCrudEditContext<IStationRouteDto>();
  const { rideOpenerId } = useContext(StationRouteContext);
  const { openEditFormDrawer } = useDrawers();
  const { hasPermissions: hasUpdateStationPermission } = useHasPermissions({
    entity: "station",
    action: "update",
  });

  const rideId = useMemo(() => {
    return rideOpenerId || stationRouteDto?.metadata?.sourceRideId;
  }, [rideOpenerId, stationRouteDto?.metadata?.sourceRideId]);

  const stationRouteDuration = useMemo(() => {
    const durationMin = getStationRouteDurationMin(stationRouteDto, timezone);

    if (!durationMin) {
      return NO_VALUE_DISPLAY;
    }

    return `${durationMin}  ${t("general.MIN")}`;
  }, [stationRouteDto, t, timezone]);

  const providerLabel = useMemo(() => {
    const provider = resolveStationRouteDirectionsService(
      stationRouteDto?.metadata,
    );
    if (!provider) {
      return null;
    }
    const providerOption = PROVIDER_OPTIONS.find(opt => opt.value === provider);
    return providerOption?.label || provider;
  }, [stationRouteDto?.metadata]);

  const handleStationClick = (stationId?: number): void => {
    if (hasUpdateStationPermission && stationId) {
      openEditFormDrawer({
        entity: "station",
        id: stationId,
      });
    }
  };

  const canOpenOrigin =
    hasUpdateStationPermission && !!stationRouteDto?.originStationId;
  const canOpenDestination =
    hasUpdateStationPermission && !!stationRouteDto?.destinationStationId;

  return (
    <HeaderLine2Styled>
      <TabsBottomInfoItemStyled>
        <label className="label">{t("stationRoute.formLabel.id")}</label>
        <label data-e2e="stationRoute-id" className="info">
          {stationRouteDto?.id || t("selectListLabel.New")}
        </label>
      </TabsBottomInfoItemStyled>

      <TabsBottomInfoItemStyled>
        <label data-e2e="stationRoute-distance">
          {stationRouteDto?.result?.totalDistance
            ? distanceFormula(stationRouteDto.result.totalDistance) +
              " " +
              t(`general.KM`)
            : NO_VALUE_DISPLAY}
        </label>
      </TabsBottomInfoItemStyled>
      <TabsBottomInfoItemStyled className="teal">
        <label data-e2e="stationRoute-duration">{stationRouteDuration}</label>
      </TabsBottomInfoItemStyled>

      <TabsBottomInfoItemStyled>
        <div className="item__content station-route-header-station">
          <label className="label">
            {t("stationRoute.formLabel.originStationId")}
          </label>
          <FramerTooltip
            placement={PopoverPlacement.bottom}
            content={stationRouteDto?.originStationName || NO_VALUE_DISPLAY}
          >
            <span
              data-e2e="origin-station-name"
              className={clsx(
                "station-route-header-station-name",
                "truncate-text",
                { pointer: canOpenOrigin },
              )}
              role={canOpenOrigin ? "button" : undefined}
              tabIndex={canOpenOrigin ? 0 : undefined}
              onClick={() =>
                handleStationClick(stationRouteDto?.originStationId)
              }
              onKeyDown={onActivateKeyDown(() =>
                handleStationClick(stationRouteDto?.originStationId),
              )}
            >
              {stationRouteDto?.originStationName || NO_VALUE_DISPLAY}
            </span>
          </FramerTooltip>
        </div>
      </TabsBottomInfoItemStyled>
      <TabsBottomInfoItemStyled>
        <div className="item__content station-route-header-station">
          <label className="label">
            {t("stationRoute.formLabel.destinationStationId")}
          </label>
          <FramerTooltip
            placement={PopoverPlacement.bottom}
            content={
              stationRouteDto?.destinationStationName || NO_VALUE_DISPLAY
            }
          >
            <span
              data-e2e="destination-station-name"
              className={clsx(
                "station-route-header-station-name",
                "truncate-text",
                { pointer: canOpenDestination },
              )}
              role={canOpenDestination ? "button" : undefined}
              tabIndex={canOpenDestination ? 0 : undefined}
              onClick={() =>
                handleStationClick(stationRouteDto?.destinationStationId)
              }
              onKeyDown={onActivateKeyDown(() =>
                handleStationClick(stationRouteDto?.destinationStationId),
              )}
            >
              {stationRouteDto?.destinationStationName || NO_VALUE_DISPLAY}
            </span>
          </FramerTooltip>
        </div>
      </TabsBottomInfoItemStyled>
      {Boolean(rideId) && (
        <TabsBottomInfoItemStyled>
          <label className="label">{t("stationRoute.formLabel.rideId")}</label>
          <label data-e2e="stationRoute-rideId" className="info grey">
            {rideId}
          </label>
        </TabsBottomInfoItemStyled>
      )}
      {providerLabel && (
        <TabsBottomInfoItemStyled className="station-route-header-map-provider">
          <label className="label">
            {t("stationRoute.formLabel.mapProvider")}
          </label>
          <label data-e2e="stationRoute-provider" className="info">
            {providerLabel}
          </label>
        </TabsBottomInfoItemStyled>
      )}
    </HeaderLine2Styled>
  );
};
